import { isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { CookiesService } from '@app/core/services/cookies.service';
import * as fromRoot from '@app/reducers';
import { pageAnimation } from '@app/shared/animations/page-animations';
import { ThemeSimplified } from '@app/shared/helpers/theme';
import { Device } from '@app/shared/models/device';
import { TransferFile } from '@app/shared/models/transferFile';
import { select, Store } from '@ngrx/store';
import Plyr, { Options, SourceInfo } from 'plyr';
import { Observable, Subscription } from 'rxjs';
import { Constant } from 'src/constant';
import { ViewerActions } from '../actions';
import * as fromViewer from '../reducers/viewer.reducer';
import { ResolveThemeService } from '@app/core/services/resolvers/resolve-theme.service';
import { hostnameIsFromMainDomain } from '@app/shared/helpers/location';
import { GetTransferPreviewOutput } from '@smash-sdk/transfer/ejs/01-2024';


@Component({
  selector: 'app-cover',
  templateUrl: './cover.component.html',
  styleUrls: ['./cover.component.scss'],
  animations: [
    pageAnimation,
  ]
})
export class CoverComponent implements OnInit, OnDestroy, OnChanges {

  @Input() preview: GetTransferPreviewOutput;
  @Input() device: Device;
  @Input() files: TransferFile[];
  @Input() countdownDisplayed: boolean;
  @Input() isDownloading: boolean;
  @Input() countryCode: string;
  @Input() filesReady: boolean;
  @Output() triggerCoverCLick: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() videoReadyToPlay: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() backToPreview: EventEmitter<boolean> = new EventEmitter();

  menuDisplayed$: Observable<boolean>;
  selectedLanguage$: Observable<string>;
  theme: ThemeSimplified;
  themeLoading = false;
  showSeeFiles = false;
  seeFilesLineResize = false;
  currentTooltipState: 'in' | 'out' = 'in';
  showVideo = false;
  playerSource: SourceInfo;
  playerOptions: Options;
  isMainDomain = false;
  subscriptions: Subscription[] = [];
  constructor(
    private readonly store$: Store<fromRoot.State & fromViewer.State>,
    private resolveThemeService: ResolveThemeService,
    private cookiesService: CookiesService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.menuDisplayed$ = this.store$.pipe(select(fromViewer.getMenuDisplayed));
    this.selectedLanguage$ = this.store$.pipe(select(fromRoot.getSelectedLanguage));

    if (isPlatformBrowser(this.platformId)) {
      if (hostnameIsFromMainDomain(location.hostname)) {
        this.isMainDomain = true;
        this.themeLoading = true;
        this.subscriptions.push(
          this.resolveThemeService.resolve({ target: 'External' }).subscribe(({ theme, }) => {
            this.themeLoading = false;
            this.theme = theme;
          })
        );
      }
    }
  };

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.startIntervalTooltipInfos();
    }
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => subscription.unsubscribe())
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.preview && isPlatformBrowser(this.platformId)) {
      this.startSeeFilesAnimation();
    }

    if (!!changes?.preview?.currentValue) {
      this.setupPlayer();
    }
  }

  get displayThemeSpinner(): boolean {
    return !!this.theme;
  }

  get displayTransferPreviewSpinner(): boolean {
    return !this.theme && !!this.preview;
  }

  get isPreviewDisable(): boolean {
    return this.preview?.transfer?.preview === Constant.previewStatus.None;
  }

  get isTransferAvailable() {
    return this.preview?.transfer?.status === Constant.transferStatus.Uploaded;
  }

  onToolTipClick(): void { // 
    if (this.preview?.transfer?.status === Constant.transferStatus.Uploaded) {
      // ensure files are loaded
      this.store$.dispatch(ViewerActions.RequestDownloadUrls({}));
      this.store$.dispatch(ViewerActions.DisplayDownloadSection());
    }
  }

  showPreview(): void {
    this.triggerCoverCLick.emit();
  }

  onToggleMenu(event) {
    this.store$.dispatch(ViewerActions.ToggleMenu({ menuDisplayed: event.toggled }));
    event.event?.stopPropagation();
    return false;
  }

  private startIntervalTooltipInfos(): void {
    setInterval(() => {
      this.currentTooltipState = this.currentTooltipState === 'in' ? 'out' : 'in';
    }, 3000);
  }

  /* Start animation "view files" on the bottom of the cover */
  private startSeeFilesAnimation() {
    setTimeout(() => {
      this.seeFilesLineResize = true;
    }, 800);
    setTimeout(() => {
      this.showSeeFiles = true;
    }, 1200);
  }

  isVideoBgReadyToPlay(event): void {
    if (isPlatformBrowser(this.platformId)) {
      this.videoReadyToPlay.emit(event);
      setTimeout(() => {
        this.showVideo = true;
      }, 500);
    }
  }

  get themeBackgroundHtmlIdentifier(): string {
    return this.theme?.id ? `customization_background_${this.theme.id}` : '';
  }

  // Player
  setupPlayer(): void {
    if (Constant.videoProviders[this.theme?.teasing?.background?.type]) {
      this.playerSource = this.buildPlayerSource({ provider: this.theme.teasing.background.type, src: this.theme.teasing.background.url });
      this.playerOptions = this.buildPlayerOptions({ provider: this.theme.teasing.background.type });
    } else if (this.preview?.transfer?.customization?.background?.type === Constant.providerPlayerTypes.vimeo || this.preview?.transfer?.customization?.background?.type === Constant.providerPlayerTypes.youtube) {
      this.playerSource = this.buildPlayerSource({ provider: this.preview.transfer.customization.background.type, src: this.preview.transfer.customization.background.sourceId });
      this.playerOptions = this.buildPlayerOptions({ provider: this.preview.transfer.customization.background.type });
    }
  }

  buildPlayerSource({ provider, src }): SourceInfo {
    const source: SourceInfo = {
      sources: [{
        src,
        provider: Constant.videoProviders[provider],
      }],
      type: 'video' as Plyr.MediaType,
    }
    return source;
  }

  buildPlayerOptions({ provider }): Options {
    const options: Options = {
      autoplay: true,
      clickToPlay: false,
      controls: [],
      volume: 0,
      storage: { enabled: false },
      loop: { active: true },
    };

    if (provider === Constant.providerPlayerTypes.youtube) {
      options.youtube = { noCookie: true, rel: 0, controls: 0, autohide: 1, showinfo: 0, iv_load_policy: 3, modestbranding: 1 };
    }

    if (provider === Constant.providerPlayerTypes.vimeo) {
      options.vimeo = { dnt: 1, byline: 0, title: 0, background: 1, loop: 1, autoplay: 1, muted: 1 };
    }
    return options;
  }
}

